/**
 * Add Medicine Page - Create new medicine records
 * Mapped to backend Medicine.java entity with exact field names
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, Pill } from 'lucide-react';
import { medicineApi, type Medicine } from '../../services/gaushala/api';
import { getLoggedInUserGaushalaId } from '../../utils/auth';

interface MedicineFormData {
  name: string;
  description: string;
  type: string;
  unit: string;
  expiryDate: string;
  manufacturer: string;
  batchNumber: string;
  purpose: string;
}

// Custom field components defined outside to prevent re-creation on every render
const InputField = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  error,
  className = '',
  ...props
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
      {...props}
    />
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
);

const SelectField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  options,
  className = ''
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((option: any) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
);

const TextAreaField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  rows = 3,
  className = ''
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
    />
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
);

export default function AddMedicine() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Validation constraints
  const VALIDATION_RULES = {
    name: { min: 2, max: 100 },
    batchNumber: { min: 0, max: 50 },
    manufacturer: { min: 0, max: 100 },
    purpose: { min: 0, max: 500 },
    description: { min: 0, max: 1000 }
  };

  const [formData, setFormData] = useState<MedicineFormData>({
    name: '',
    description: '',
    type: '',
    unit: 'mg',
    expiryDate: '',
    manufacturer: '',
    batchNumber: '',
    purpose: ''
  });

  const [errors, setErrors] = useState<Partial<MedicineFormData>>({});

  const handleInputChange = (field: keyof MedicineFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof MedicineFormData, string>> = {};

    // Medicine Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Medicine Name is required';
    } else if (formData.name.trim().length < VALIDATION_RULES.name.min) {
      newErrors.name = `Medicine Name must be at least ${VALIDATION_RULES.name.min} characters`;
    } else if (formData.name.length > VALIDATION_RULES.name.max) {
      newErrors.name = `Medicine Name cannot exceed ${VALIDATION_RULES.name.max} characters`;
    }

    // Medicine Type validation
    if (!formData.type.trim()) newErrors.type = 'Medicine Type is required';

    // Unit validation
    if (!formData.unit.trim()) newErrors.unit = 'Unit is required';

    // Batch Number validation (optional field, but has max length)
    if (formData.batchNumber && formData.batchNumber.length > VALIDATION_RULES.batchNumber.max) {
      newErrors.batchNumber = `Batch Number cannot exceed ${VALIDATION_RULES.batchNumber.max} characters`;
    }

    // Manufacturer validation (optional field, but has max length)
    if (formData.manufacturer && formData.manufacturer.length > VALIDATION_RULES.manufacturer.max) {
      newErrors.manufacturer = `Manufacturer cannot exceed ${VALIDATION_RULES.manufacturer.max} characters`;
    }

    // Purpose validation (optional field, but has max length)
    if (formData.purpose && formData.purpose.length > VALIDATION_RULES.purpose.max) {
      newErrors.purpose = `Purpose cannot exceed ${VALIDATION_RULES.purpose.max} characters`;
    }

    // Description validation (optional field, but has max length)
    if (formData.description && formData.description.length > VALIDATION_RULES.description.max) {
      newErrors.description = `Description cannot exceed ${VALIDATION_RULES.description.max} characters`;
    }

    // Expiry Date validation
    if (!formData.expiryDate.trim()) newErrors.expiryDate = 'Expiry Date is required';

    // Date validation - expiry date should be in the future
    if (formData.expiryDate) {
      const expiryDate = new Date(formData.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate < today) {
        newErrors.expiryDate = 'Expiry Date should be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields and fix validation errors.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // CRITICAL: Backend MedicineDTO requires specific field names and types
      // Backend expects: gaushalaId (Long), name (String), quantity (Long)
      // All fields must match backend validation exactly

      // Get user's gaushalaId from JWT token
      let userGaushalaId = getLoggedInUserGaushalaId();
      console.log('🔍 DEBUG: User gaushalaId from JWT:', userGaushalaId);

      // If not in JWT, fetch from API (fallback mechanism)
      if (!userGaushalaId) {
        console.warn('Gaushala ID not found in JWT token, fetching from API...');
        try {
          const userContext = await import('../../utils/auth').then(m => m.getCurrentUserContext());
          const userId = userContext.userId;

          const response = await fetch(
            `http://localhost:8086/gaushala-service/api/v1/gaushala/user-access/primary?userId=${userId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              }
            }
          );

          if (response.ok) {
            const gaushalaIdFromApi = await response.json();
            if (gaushalaIdFromApi) {
              userGaushalaId = gaushalaIdFromApi;
              console.log('✓ Fetched gaushalaId from API:', userGaushalaId);
            }
          }
        } catch (apiError) {
          console.warn('Failed to fetch gaushalaId from API:', apiError);
        }
      }

      // Validate gaushalaId exists (from either JWT or API)
      if (!userGaushalaId) {
        throw new Error('Gaushala ID not found. Please login again.');
      }

      const medicineData = {
        gaushalaId: Number(userGaushalaId), // REQUIRED: Long type (from user context)
        name: formData.name.trim(), // REQUIRED: String, 2-255 chars
        description: formData.description.trim() || null, // OPTIONAL: String
        dosage: formData.type.trim() || null, // OPTIONAL: String (formerly type)
        unit: formData.unit.trim() || null, // OPTIONAL: String
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null, // OPTIONAL: LocalDateTime ISO format
        manufacturer: formData.manufacturer.trim() || null, // OPTIONAL: String
        batchNumber: formData.batchNumber.trim() || null, // OPTIONAL: String
        purpose: formData.purpose.trim() || null // OPTIONAL: String
      };

      // Log the exact data being sent
      console.log('=== SUBMITTING MEDICINE DATA ===');
      console.log('🔵 User GaushalaId:', userGaushalaId);
      console.log('🔵 Final gaushalaId being sent:', medicineData.gaushalaId);
      console.log('Medicine Data:', JSON.stringify(medicineData, null, 2));
      console.log('Field Types:');
      console.log('- gaushalaId:', typeof medicineData.gaushalaId, medicineData.gaushalaId);
      console.log('- name:', typeof medicineData.name, medicineData.name);
      console.log('==============================');

      const result = await medicineApi.createMedicine(medicineData as any);

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Medicine record created successfully!'
        });
        console.log('✓ Medicine created successfully:', result.data);

        // Navigate back after delay
        setTimeout(() => {
          navigate('/gaushala/medicine');
        }, 2000);
      } else {
        // Display the actual error from backend
        const errorMessage = result.error || 'Failed to create medicine record.';
        console.error('✗ Backend error:', errorMessage);
        setMessage({
          type: 'error',
          text: errorMessage
        });
      }
    } catch (error) {
      let errorMsg = 'An unexpected error occurred. Please check browser console.';

      if (error instanceof Error) {
        errorMsg = error.message;
        if (errorMsg.includes('Gaushala ID not found')) {
          errorMsg = 'Gaushala ID not found. Please ensure your user has proper gaushala access configured in admin panel. Please logout and login again.';
        }
      } else if (typeof error === 'string') {
        errorMsg = error;
      } else if (error && typeof error === 'object') {
        errorMsg = JSON.stringify(error);
      }

      console.error('✗ Exception during submit:', errorMsg);
      console.error('✗ Full error object:', error);

      setMessage({
        type: 'error',
        text: errorMsg
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/medicine');
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add New Medicine</h1>
            <p className="text-gray-600 mt-1">Create new medicine inventory record</p>
          </div>
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <span className="text-green-500">✓</span>
              ) : (
                <span className="text-red-500">⚠</span>
              )}
              {message.text}
            </div>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Pill className="h-5 w-5 text-blue-600" />
              </div>
              Medicine Information
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Medicine Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Medicine Name <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 font-normal ml-2">({formData.name.length}/{VALIDATION_RULES.name.max})</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    if (e.target.value.length <= VALIDATION_RULES.name.max) {
                      handleInputChange('name', e.target.value);
                    }
                  }}
                  maxLength={VALIDATION_RULES.name.max}
                  placeholder="Enter medicine name (min 2 characters)"
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.name && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Batch Number */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Batch Number
                  <span className="text-xs text-gray-500 font-normal ml-2">({formData.batchNumber.length}/{VALIDATION_RULES.batchNumber.max})</span>
                </label>
                <input
                  type="text"
                  value={formData.batchNumber}
                  onChange={(e) => {
                    if (e.target.value.length <= VALIDATION_RULES.batchNumber.max) {
                      handleInputChange('batchNumber', e.target.value);
                    }
                  }}
                  maxLength={VALIDATION_RULES.batchNumber.max}
                  placeholder="Enter batch number (optional)"
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                    errors.batchNumber ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.batchNumber && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.batchNumber}
                  </div>
                )}
              </div>

              {/* Medicine Type */}
              <SelectField
                label="Medicine Type"
                value={formData.type}
                onChange={(value: string) => handleInputChange('type', value)}
                placeholder="Select medicine type"
                required
                error={errors.type}
                options={[
                  { value: 'Tablets', label: 'Tablets' },
                  { value: 'Capsules', label: 'Capsules' },
                  { value: 'Injections', label: 'Injections' },
                  { value: 'Syrups', label: 'Syrups' },
                  { value: 'Creams', label: 'Creams' },
                  { value: 'Ointments', label: 'Ointments' },
                  { value: 'Powders', label: 'Powders' },
                  { value: 'Drops', label: 'Drops' },
                  { value: 'Gels', label: 'Gels' },
                  { value: 'Sprays', label: 'Sprays' }
                ]}
              />

              {/* Unit */}
              <SelectField
                label="Unit"
                value={formData.unit}
                onChange={(value: string) => handleInputChange('unit', value)}
                placeholder="Select unit"
                required
                error={errors.unit}
                options={[
                  { value: 'mg', label: 'Milligrams (mg)' },
                  { value: 'mcg', label: 'Micrograms (mcg)' },
                  { value: 'g', label: 'Grams (g)' },
                  { value: 'ml', label: 'Milliliters (ml)' },
                  { value: 'l', label: 'Liters (l)' },
                  { value: 'iu', label: 'International Units (IU)' },
                  { value: 'mmol', label: 'Millimoles (mmol)' }
                ]}
              />

              {/* Expiry Date */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Expiry Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                      errors.expiryDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                {errors.expiryDate && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.expiryDate}
                  </div>
                )}
              </div>

              {/* Manufacturer */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Manufacturer
                  <span className="text-xs text-gray-500 font-normal ml-2">({formData.manufacturer.length}/{VALIDATION_RULES.manufacturer.max})</span>
                </label>
                <input
                  type="text"
                  value={formData.manufacturer}
                  onChange={(e) => {
                    if (e.target.value.length <= VALIDATION_RULES.manufacturer.max) {
                      handleInputChange('manufacturer', e.target.value);
                    }
                  }}
                  maxLength={VALIDATION_RULES.manufacturer.max}
                  placeholder="Enter manufacturer name (optional)"
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                    errors.manufacturer ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.manufacturer && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.manufacturer}
                  </div>
                )}
              </div>

              {/* Purpose */}
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Purpose
                  <span className="text-xs text-gray-500 font-normal ml-2">({formData.purpose.length}/{VALIDATION_RULES.purpose.max})</span>
                </label>
                <textarea
                  value={formData.purpose}
                  onChange={(e) => {
                    if (e.target.value.length <= VALIDATION_RULES.purpose.max) {
                      handleInputChange('purpose', e.target.value);
                    }
                  }}
                  maxLength={VALIDATION_RULES.purpose.max}
                  placeholder="Enter purpose or indication for use (optional)"
                  rows={2}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
                    errors.purpose ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.purpose && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.purpose}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Description
                  <span className="text-xs text-gray-500 font-normal ml-2">({formData.description.length}/{VALIDATION_RULES.description.max})</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    if (e.target.value.length <= VALIDATION_RULES.description.max) {
                      handleInputChange('description', e.target.value);
                    }
                  }}
                  maxLength={VALIDATION_RULES.description.max}
                  placeholder="Enter additional details about the medicine (optional)"
                  rows={3}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
                    errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.description}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-start gap-4 mt-8 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                {isLoading ? 'Creating...' : 'Create Medicine'}
              </button>
            </div>
          </form>
        </div>
      </div>
  );
}
